import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import styles from './styles.module.scss';

import ArchiveCardSkeleton from 'OK/components/archiveCardSkeleton';
import Button from 'OK/components/button';
import Input from 'OK/components/input';
import CardLayout from 'OK/components/layouts/card';
import ContentLayout from 'OK/components/layouts/content';
import TextLayout from 'OK/components/layouts/content/text';
import Link from 'OK/components/link';
import PageTitle from 'OK/components/pageTitle';
import SolutionItem from 'OK/components/solutions/solutionItem';
import Text from 'OK/components/text';
import StaticPagesMenu from 'OK/modules/staticPagesMenu';
import { getAllFeaturesByCategoryAndSubType } from 'OK/networking/solutions';
import { CALENDLY_LINK } from 'OK/util/constants';
import useAuthentication from 'OK/util/hooks/useAuthentication';
import useI18n from 'OK/util/hooks/useI18n';

export default function Solutions() {
  const { locale } = useI18n();

  const [authenticated] = useAuthentication(() => false);

  const activeOrganisationId = useSelector((state) => state.account.activeOrganisationId);
  const isInstalledAsPWA = useSelector((state) => state.app.isInstalledAsPWA);


  // State
  const [functions, setFunctions] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [hasActiveOrganisation, setHasActiveOrganisation] = useState(false);
  const [promptInstall, setPromptInstall] = useState(null);
  const [supportsPWA, setSupportsPWA] = useState(false);

  /* Methods */

  const handleInstallPWALink = (event) => {
    event.preventDefault();

    if (!promptInstall) {
      return;
    }

    promptInstall.prompt();
  };


  useEffect(() => {
    setIsDataLoading(true);

    getAllFeaturesByCategoryAndSubType(locale).then((res) => {
      if (res.responseData) {
        setIndustries(res.responseData.filter((e) => e.categoryType === 'Industry'));
        setFunctions(res.responseData.filter((e) => e.categoryType === 'Function'));
      }

      setIsDataLoading(false);
    });
  }, [locale]);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setSupportsPWA(true);
      setPromptInstall(e);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('transitionend', handler);
  }, []);

  useEffect(() => {
    if (activeOrganisationId !== null) {
      setHasActiveOrganisation(true);
    }
  }, [activeOrganisationId]);

  /* Render */

  return (
    <>
      <PageTitle>Welcome to OK</PageTitle>
      <StaticPagesMenu currentSectionTitle='Help' currentTabId='solutions' />
      <div className={styles.contentContainer}>
        <div className={styles.headerContainer}>
          <ContentLayout className={styles.headerSection} pageContent>
            <TextLayout>
              <h2 className={styles.pageHeader}>Welcome to OK</h2>
            </TextLayout>
            <div className={styles.solutionsCardsContainer}>
              <></>
              <div className={styles.solutionsIntroductionCard} fixedWidth={false}>
                <h2 className={styles.solutionsCardHeader}>
                  Explore our AI-powered supply chain and site monitoring solutions.
                </h2>
                <Text size='md'>
                  <span style={{ fontWeight: 'bold' }}>Want a demo, help or intro?</span>
                  <br />
                  We&apos;re here 24/7, 365 days a year.
                </Text>
                <Button
                  className={styles.solutionsButton}
                  icon='/icons/caret_dark.svg'
                  iconStyle={styles.bookCaret}
                  tint='creation'
                  onClick={() => window.open(CALENDLY_LINK, '_blank')}
                >
                  Book a free personal session
                </Button>
                <Button
                  className={`intercom ${styles.solutionsButton}`}
                  icon='/icons/messaging_dark.svg'
                  iconStyle={styles.conversationIcon}
                  tint='navigation'
                >
                  Talk to us now via live chat
                </Button>
                <Link
                  href='/solutions/contact'
                  style={{ color: 'white', fontWeight: 'normal', marginTop: 20, width: 233 }}
                  tint='navigation'
                  withCaret
                >
                  More ways to connect
                </Link>
              </div>
              <CardLayout className={styles.getStartedCard} fixedWidth={false}>
                <h3 style={{ fontSize: 16 }}>New to OK?</h3>
                <div className={styles.getStartedCardContent}>
                  <div className={styles.getStartedSteps}>
                    <div className={authenticated ? styles.getStartedStep1Authenticated : styles.getStartedStep1}>
                      {authenticated ? <img src='/icons/check_green.svg' alt="You're logged into OK." /> : '1'}
                    </div>
                    <div
                      className={authenticated ? styles.getStartedStepLineAuthenticated : styles.getStartedStepLine}
                    ></div>
                    <div
                      className={hasActiveOrganisation ? styles.getStartedStep2Authenticated : styles.getStartedStep2}
                    >
                      {activeOrganisationId !== null ? (
                        <img src='/icons/check_green.svg' alt="You're in an OK organisation." />
                      ) : (
                        '2'
                      )}
                    </div>
                    <div
                      className={
                        hasActiveOrganisation ? styles.getStartedStepLineAuthenticated : styles.getStartedStepLine
                      }
                    ></div>
                    <div className={isInstalledAsPWA ? styles.getStartedStep1Authenticated : styles.getStartedStep1}>
                      {isInstalledAsPWA ? (
                        <img src='/icons/check_green.svg' alt="You've installed OK as a PWA." />
                      ) : (
                        '3'
                      )}
                    </div>
                  </div>
                  <div className={styles.getStartedContent}>
                    {authenticated ? (
                      <Text size='md' className={styles.getStartedContentItemStep1}>
                        You&apos;re signed in
                      </Text>
                    ) : (
                      <Link className={styles.getStartedContentItemStep1Link} href='/auth'>
                        You&apos;re not signed in
                      </Link>
                    )}
                    {activeOrganisationId !== null ? (
                      <Text size='md' className={styles.getStartedContentItemStep2}>
                        You&apos;re in an organisation
                      </Text>
                    ) : (
                      <Link href='/account' className={styles.getStartedContentItemStep2Link}>
                        Join or add organisation
                      </Link>
                    )}
                    <Text size='md' className={styles.getStartedContentItem3}>
                      {isInstalledAsPWA ? (
                        'Install the OK app for faster, fullscreen access'
                      ) : supportsPWA ? (
                        <a onClick={handleInstallPWALink}>Install the OK app for faster, fullscreen access</a>
                      ) : (
                        'Install the OK app for faster, fullscreen access'
                      )}
                    </Text>
                  </div>
                </div>
              </CardLayout>
              <img alt='bear image' className={styles.bearImage} src='/img/bear.png' />
            </div>
          </ContentLayout>
        </div>
        <ContentLayout className={styles.featuresSection} pageContent>
          <div className={styles.functionSection}>
            <TextLayout>
              <h3>All your needs</h3>
            </TextLayout>
            {isDataLoading ? (
              <ContentLayout contentClassName={styles.solutionItemsGrid} pageContent>
                <ArchiveCardSkeleton listView />
                <ArchiveCardSkeleton listView />
                <ArchiveCardSkeleton listView />
                <ArchiveCardSkeleton listView />
              </ContentLayout>
            ) : (
              <ContentLayout contentClassName={styles.solutionItemsGrid} pageContent>
                {functions.map((fn) => (
                  <SolutionItem
                    title={fn.subType}
                    description={fn.shortDescription}
                    imageUrl={fn.imageURL}
                    key={fn.name}
                    href={`/solutions/${fn.slug}`}
                  />
                ))}
              </ContentLayout>
            )}
          </div>
        </ContentLayout>

        <ContentLayout className={styles.featuresSection} pageContent>
          <div>
            <CardLayout className={styles.chatCard} fixedWidth={false}>
              <div className={styles.cardActionContainer}>
                <h3>Everything will be OK</h3>
                <Text bold style={{ marginTop: 10 }}>
                  Let&apos;s have a chat
                </Text>
                <Button
                  className={styles.solutionsButton}
                  style={{ display: 'flex' }}
                  icon='/icons/caret_dark.svg'
                  iconStyle={styles.bookCaret}
                  tint='creation'
                  onClick={() => window.open(CALENDLY_LINK, '_blank')}
                >
                  Book a free demo
                </Button>
                <Button
                  className={`intercom ${styles.solutionsButton}`}
                  style={{ display: 'flex', marginBottom: 20 }}
                  icon='/icons/messaging_dark.svg'
                  iconStyle={styles.conversationIcon}
                  tint='navigation'
                >
                  Talk to us now via live chat
                </Button>
                <Text bold>Want us to reach out to you?</Text>
                <Input className={styles.emailInput} placeholder='Enter email address'></Input>
                <Button className={styles.solutionsButton} style={{ display: 'flex' }} icon='/icons/caret_dark.svg'>
                  Get back to me
                </Button>
              </div>
              <img className={styles.chatImage} alt='alt' src='/img/solutions_chat.png' />
            </CardLayout>
          </div>
          <TextLayout style={{ fontSize: 12, marginTop: 15 }}>
            By proceeding, you <b>agree</b> to our{' '}
            <Link href='/terms' style={{ marginBottom: 0 }}>
              privacy policy.
            </Link>
            <br />
            Please take a moment to review it first.
          </TextLayout>
        </ContentLayout>
        <ContentLayout className={styles.solutionsSection} pageContent>
          <div className={styles.solutionsSectionHeaderContainer}>
            <TextLayout>
              <h3>Find your industry</h3>
            </TextLayout>
          </div>
          <div className={styles.industrySection}>
            {isDataLoading ? (
              <ContentLayout contentClassName={styles.solutionItemsGrid} pageContent>
                <ArchiveCardSkeleton listView />
                <ArchiveCardSkeleton listView />
                <ArchiveCardSkeleton listView />
                <ArchiveCardSkeleton listView />
              </ContentLayout>
            ) : (
              <ContentLayout contentClassName={styles.solutionItemsGrid} pageContent>
                {industries.map((industry) => (
                  <SolutionItem
                    title={industry.subType}
                    description={industry.shortDescription}
                    imageUrl={industry.imageURL}
                    key={industry.name}
                    href={`/solutions/${industry.slug}`}
                  />
                ))}
              </ContentLayout>
            )}
          </div>
        </ContentLayout>
      </div>
    </>
  );
}

Solutions.layoutProps = {
  contentTopPadding: 'menuOnly',
};

export async function getServerSideProps({ locale }) {
  const i18nProps = await serverSideTranslations(locale, ['common']);
  return { props: { ...i18nProps } };
}
